// Firebase Firestore utilities for user settings and knowledgebase storage
import { doc, getDoc, setDoc, deleteField, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get user document reference
 * Using email as the document ID for easy lookup
 */
const getUserDocRef = (userEmail) => {
  if (!userEmail) {
    throw new Error('User email is required');
  }
  // Sanitize email to use as document ID (replace special characters)
  const sanitizedEmail = userEmail.replace(/[.#$[\]]/g, '_');
  return doc(db, 'users', sanitizedEmail);
};

/**
 * Load user settings from Firebase
 * Returns an object with settings or empty object if not found
 */
export const loadSettings = async (userEmail) => {
  try {
    const userDocRef = getUserDocRef(userEmail);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        signOffName: data.signOffName || '',
        kbLink: data.kbLink || '',
        personality: data.personality || 'professional',
        hasSeenOnboarding: data.hasSeenOnboarding || false,
      };
    }

    // Return default settings if document doesn't exist
    return {
      signOffName: '',
      kbLink: '',
      personality: 'professional',
      hasSeenOnboarding: false,
    };
  } catch (error) {
    console.error('Error loading settings from Firebase:', error);
    return {
      signOffName: '',
      kbLink: '',
      personality: 'professional',
      hasSeenOnboarding: false,
    };
  }
};

/**
 * Save user settings to Firebase
 * Uses merge to update only the provided fields
 */
export const saveSettings = async (settings, userEmail) => {
  try {
    const userDocRef = getUserDocRef(userEmail);

    // Use setDoc with merge to create or update
    await setDoc(userDocRef, settings, { merge: true });

    return { success: true };
  } catch (error) {
    console.error('Error saving settings to Firebase:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load knowledgebase content from Firebase
 */
export const loadKnowledgebase = async (userEmail) => {
  try {
    const userDocRef = getUserDocRef(userEmail);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.knowledgebase || null;
    }

    return null;
  } catch (error) {
    console.error('Error loading knowledgebase from Firebase:', error);
    return null;
  }
};

/**
 * Save knowledgebase content to Firebase
 */
export const saveKnowledgebase = async (content, userEmail) => {
  try {
    const userDocRef = getUserDocRef(userEmail);

    await setDoc(
      userDocRef,
      { knowledgebase: content },
      { merge: true }
    );

    return { success: true };
  } catch (error) {
    console.error('Error saving knowledgebase to Firebase:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete knowledgebase from Firebase
 */
export const deleteKnowledgebase = async (userEmail) => {
  try {
    const userDocRef = getUserDocRef(userEmail);

    // Remove the knowledgebase field from the document
    await updateDoc(userDocRef, {
      knowledgebase: deleteField()
    });

    return { success: true, message: 'Knowledgebase removed from Firebase' };
  } catch (error) {
    console.error('Error deleting knowledgebase from Firebase:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Find settings file (compatibility function for migration)
 * Not needed for Firebase but kept for API compatibility
 */
export const findSettingsFile = async () => {
  console.warn('findSettingsFile is deprecated with Firebase');
  return null;
};

/**
 * Find knowledgebase file (compatibility function for migration)
 * Not needed for Firebase but kept for API compatibility
 */
export const findKnowledgebaseFile = async () => {
  console.warn('findKnowledgebaseFile is deprecated with Firebase');
  return null;
};

/**
 * Get AI generation count for a user
 * Returns the current count and reset date based on billing period
 * @param {string} userEmail - User's email
 * @param {number} currentPeriodStart - Billing period start timestamp (milliseconds)
 */
export const getGenerationCount = async (userEmail, currentPeriodStart = null) => {
  try {
    const userDocRef = getUserDocRef(userEmail);
    const docSnap = await getDoc(userDocRef);

    const now = Date.now();

    if (docSnap.exists()) {
      const data = docSnap.data();

      // If we have a billing period, use it; otherwise use stored reset date
      let shouldReset = false;

      if (currentPeriodStart) {
        // Check if we're in a new billing period
        const storedPeriodStart = data.billingPeriodStart || 0;
        if (currentPeriodStart !== storedPeriodStart) {
          shouldReset = true;
        }
      } else if (data.generationResetDate) {
        // Fallback: Check if 30 days have passed (for users without subscription)
        const resetDate = new Date(data.generationResetDate).getTime();
        const daysPassed = (now - resetDate) / (1000 * 60 * 60 * 24);
        if (daysPassed >= 30) {
          shouldReset = true;
        }
      } else {
        shouldReset = true; // No reset date, initialize
      }

      if (shouldReset) {
        // Reset counter for new billing period
        await updateDoc(userDocRef, {
          generationCount: 0,
          billingPeriodStart: currentPeriodStart || now,
          generationResetDate: new Date(currentPeriodStart || now).toISOString()
        });

        return {
          count: 0,
          billingPeriodStart: currentPeriodStart || now,
          resetDate: new Date(currentPeriodStart || now).toISOString()
        };
      }

      return {
        count: data.generationCount || 0,
        billingPeriodStart: data.billingPeriodStart || currentPeriodStart,
        resetDate: data.generationResetDate || new Date(now).toISOString()
      };
    }

    // Initialize for new user
    await setDoc(
      userDocRef,
      {
        generationCount: 0,
        billingPeriodStart: currentPeriodStart || now,
        generationResetDate: new Date(currentPeriodStart || now).toISOString()
      },
      { merge: true }
    );

    return {
      count: 0,
      billingPeriodStart: currentPeriodStart || now,
      resetDate: new Date(currentPeriodStart || now).toISOString()
    };
  } catch (error) {
    console.error('Error getting generation count from Firebase:', error);
    return {
      count: 0,
      billingPeriodStart: null,
      resetDate: null,
      error: error.message
    };
  }
};

/**
 * Increment AI generation count for a user
 * Automatically resets counter if it's a new billing period
 * @param {string} userEmail - User's email
 * @param {number} currentPeriodStart - Billing period start timestamp (milliseconds)
 */
export const incrementGenerationCount = async (userEmail, currentPeriodStart = null) => {
  try {
    const userDocRef = getUserDocRef(userEmail);

    // First check if we need to reset for a new billing period
    const currentData = await getGenerationCount(userEmail, currentPeriodStart);

    // Increment the counter
    await updateDoc(userDocRef, {
      generationCount: increment(1),
      lastGenerationDate: new Date().toISOString()
    });

    return {
      success: true,
      newCount: currentData.count + 1
    };
  } catch (error) {
    console.error('Error incrementing generation count:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if user has reached their generation limit
 * Returns true if user can generate, false if limit reached
 * @param {string} userEmail - User's email
 * @param {string} subscriptionPlan - User's subscription plan
 * @param {number} currentPeriodStart - Billing period start timestamp (milliseconds)
 */
export const canUserGenerate = async (userEmail, subscriptionPlan = null, currentPeriodStart = null) => {
  try {
    if (userEmail && userEmail.toLowerCase() === 'support@qaly.co') {
      return {
        canGenerate: true,
        currentCount: 0,
        limit: Infinity,
        remaining: Infinity
      };
    }

    const { count } = await getGenerationCount(userEmail, currentPeriodStart);

    // Define limits based on subscription plan
    const limits = {
      free: 0,            // No AI generations for free users
      basic: 0,           // No AI generations for basic
      pro: 500,           // 500 generations per month for pro
      unlimited: Infinity // No limit for specific plans
    };

    // Default to free if no subscription
    const plan = subscriptionPlan || 'free';
    const limit = limits[plan.toLowerCase()] || limits.free;

    return {
      canGenerate: count < limit,
      currentCount: count,
      limit: limit,
      remaining: Math.max(0, limit - count)
    };
  } catch (error) {
    console.error('Error checking generation limit:', error);
    // Default to allowing generation on error
    return {
      canGenerate: true,
      currentCount: 0,
      limit: 0,
      remaining: 0,
      error: error.message
    };
  }
};
